import { c as _c } from "react/compiler-runtime";
// «Следующий шаг» и задачи заявки. Заявка без следующего шага — та, о которой
// забыли, поэтому ближайшая открытая задача показана крупно, а просроченная —
// красным; закрыть её можно одной кнопкой, не открывая форму касания.
import { CRM_TASK_KINDS, CRM_TASK_KIND_LABELS, type CrmTask, type CrmTaskInput, type CrmTaskKind } from "@osmix/contracts";
import { Button } from "@osmix/ui/button";
import { Input } from "@osmix/ui/input";
import { CalendarClock, Check, CheckCircle2, Circle, ListTodo, Mail, MessageCircle, Phone, Plus, Send, Trash2, Upload, Users, type LucideIcon, FolderInput, Flag } from "lucide-react";
import { useState } from "react";
import type { ApplicationAssignee } from "@/api";
import { useAddTask, useDeleteTask, useUpdateTask } from "@/api/hooks/use-application-crm";
import { Select } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { DuePicker, presetDue } from "./due-picker";
import { errorText, formatDayTime, formatDue, overdueBy } from "./format";
import { FieldError, Panel, Spinner } from "./ui";
export const TASK_ICON: Record<CrmTaskKind, LucideIcon> = {
  call: Phone,
  meeting: Users,
  email: Mail,
  message: MessageCircle,
  send_offer: Send,
  collect_materials: FolderInput,
  upload_catalog: Upload,
  other: Flag
};

/** Срок задачи одной строкой; просроченная — красным и с «насколько». */
export function DueLabel(t0) {
  const $ = _c(16);
  const {
    task,
    className
  } = t0;
  if (task.doneAt) {
    let t1;
    if ($[0] !== className) {
      t1 = cn("text-muted-foreground", className);
      $[0] = className;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== task.doneAt) {
      t2 = formatDayTime(task.doneAt);
      $[2] = task.doneAt;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    let t3;
    if ($[4] !== t1 || $[5] !== t2) {
      t3 = <span className={t1}>выполнено {t2}</span>;
      $[4] = t1;
      $[5] = t2;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    return t3;
  }
  const t1 = task.overdue ? "font-medium text-destructive" : "text-muted-foreground";
  let t2;
  if ($[7] !== className || $[8] !== t1) {
    t2 = cn(t1, className);
    $[7] = className;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== task.dueAt || $[11] !== task.overdue) {
    t3 = task.overdue ? `${overdueBy(task.dueAt)} · ${formatDayTime(task.dueAt)}` : formatDue(task.dueAt);
    $[10] = task.dueAt;
    $[11] = task.overdue;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== t2 || $[14] !== t3) {
    t4 = <span className={t2}>{t3}</span>;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
}
export function NextStepPanel(t0) {
  const $ = _c(52);
  const {
    applicationId,
    task,
    assignees,
    canManage
  } = t0;
  const update = useUpdateTask();
  const [adding, setAdding] = useState(false);
  const [moving, setMoving] = useState(false);
  const [error, setError] = useState(null);
  if (!task) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <CalendarClock className="size-4 text-muted-foreground" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== adding || $[2] !== applicationId || $[3] !== assignees || $[4] !== canManage) {
      t2 = <Panel title={"\u0421\u043B\u0435\u0434\u0443\u044E\u0449\u0438\u0439 \u0448\u0430\u0433"} icon={t1} className="border-amber-500/40" data-testid="crm-next-step">{adding ? <TaskForm applicationId={applicationId} assignees={assignees} onDone={() => setAdding(false)} /> : <div className="flex flex-col gap-2"><p className="text-sm text-amber-700 dark:text-amber-500">Не назначен — о заявке легко забыть.</p>{canManage && <Button size="sm" className="self-start" onClick={() => setAdding(true)}><Plus className="size-4" />Поставить следующий шаг</Button>}</div>}</Panel>;
      $[1] = adding;
      $[2] = applicationId;
      $[3] = assignees;
      $[4] = canManage;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    return t2;
  }
  const Icon = TASK_ICON[task.kind];
  let t1;
  if ($[6] !== applicationId || $[7] !== task.id || $[8] !== update) {
    t1 = next => {
      setError(null);
      update.mutate({
        applicationId,
        taskId: task.id,
        patch: next
      }, {
        onError: e => setError(errorText(e)),
        onSuccess: () => setMoving(false)
      });
    };
    $[6] = applicationId;
    $[7] = task.id;
    $[8] = update;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const patch = t1;
  let t2;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <CalendarClock className="size-4 text-muted-foreground" />;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const t3 = task.overdue && "border-destructive/50 bg-destructive/5";
  let t4;
  if ($[11] !== t3) {
    t4 = cn(t3);
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const t5 = task.overdue ? "bg-destructive/10 text-destructive" : "bg-primary/10 text-primary";
  let t6;
  if ($[13] !== t5) {
    t6 = cn("flex size-9 shrink-0 items-center justify-center rounded-full", t5);
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== Icon) {
    t7 = <Icon className="size-4" />;
    $[15] = Icon;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t6 || $[18] !== t7) {
    t8 = <span className={t6}>{t7}</span>;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== task.title) {
    t9 = <p className="text-base leading-snug font-semibold break-words">{task.title}</p>;
    $[20] = task.title;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== task) {
    t10 = <p className="mt-0.5 text-sm"><DueLabel task={task} /></p>;
    $[22] = task;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const t11 = CRM_TASK_KIND_LABELS[task.kind];
  const t12 = task.assignee && ` · ${task.assignee.name}`;
  let t13;
  if ($[24] !== t11 || $[25] !== t12) {
    t13 = <p className="text-xs text-muted-foreground">{t11}{t12}</p>;
    $[24] = t11;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t10 || $[28] !== t13 || $[29] !== t9) {
    t14 = <div className="min-w-0 flex-1">{t9}{t10}{t13}</div>;
    $[27] = t10;
    $[28] = t13;
    $[29] = t9;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t14 || $[32] !== t8) {
    t15 = <div className="flex items-start gap-3">{t8}{t14}</div>;
    $[31] = t14;
    $[32] = t8;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== canManage || $[35] !== patch || $[36] !== update.isPending) {
    t16 = canManage && <div className="flex flex-wrap gap-2"><Button size="sm" disabled={update.isPending} onClick={() => patch({
        done: true
      })}>{update.isPending ? <Spinner /> : <Check className="size-4" />}Выполнено</Button><Button size="sm" variant="outline" disabled={update.isPending} onClick={() => setMoving(_temp)}>Перенести</Button></div>;
    $[34] = canManage;
    $[35] = patch;
    $[36] = update.isPending;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== moving || $[39] !== patch || $[40] !== update.isPending) {
    t17 = moving && <div className="flex flex-wrap gap-1.5">{([["today", "\u041D\u0430 \u0441\u0435\u0433\u043E\u0434\u043D\u044F"], ["tomorrow", "\u041D\u0430 \u0437\u0430\u0432\u0442\u0440\u0430"], ["in3", "\u0427\u0435\u0440\u0435\u0437 3 \u0434\u043D\u044F"], ["week", "\u0427\u0435\u0440\u0435\u0437 \u043D\u0435\u0434\u0435\u043B\u044E"]] as const).map(t18 => {
        const [preset, label] = t18;
        return <Button key={preset} size="xs" variant="outline" disabled={update.isPending} onClick={() => patch({
          dueAt: presetDue(preset)
        })}>{label}</Button>;
      })}</div>;
    $[38] = moving;
    $[39] = patch;
    $[40] = update.isPending;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== error) {
    t18 = <FieldError>{error}</FieldError>;
    $[42] = error;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== t15 || $[45] !== t16 || $[46] !== t17 || $[47] !== t18) {
    t19 = <div className="flex flex-col gap-3">{t15}{t16}{t17}{t18}</div>;
    $[44] = t15;
    $[45] = t16;
    $[46] = t17;
    $[47] = t18;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== t19 || $[50] !== t4) {
    t20 = <Panel title={"\u0421\u043B\u0435\u0434\u0443\u044E\u0449\u0438\u0439 \u0448\u0430\u0433"} icon={t2} className={t4} data-testid="crm-next-step">{t19}</Panel>;
    $[49] = t19;
    $[50] = t4;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  return t20;
}
function _temp(v) {
  return !v;
}
export function TasksPanel(t0) {
  const $ = _c(52);
  const {
    applicationId,
    tasks,
    assignees,
    canManage
  } = t0;
  const update = useUpdateTask();
  const remove = useDeleteTask(applicationId);
  const [adding, setAdding] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const [error, setError] = useState(null);
  let T0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== adding || $[1] !== applicationId || $[2] !== assignees || $[3] !== canManage || $[4] !== remove || $[5] !== showDone || $[6] !== tasks || $[7] !== update) {
    const open = tasks.filter(_temp2);
    const done = tasks.filter(_temp3);
    let t10;
    if ($[18] !== applicationId || $[19] !== update) {
      t10 = task_1 => {
        setError(null);
        update.mutate({
          applicationId,
          taskId: task_1.id,
          patch: {
            done: !task_1.doneAt
          }
        }, {
          onError: e => setError(errorText(e))
        });
      };
      $[18] = applicationId;
      $[19] = update;
      $[20] = t10;
    } else {
      t10 = $[20];
    }
    const toggle = t10;
    T0 = Panel;
    t6 = "\u0417\u0430\u0434\u0430\u0447\u0438";
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <ListTodo className="size-4 text-muted-foreground" />;
      $[21] = t7;
    } else {
      t7 = $[21];
    }
    t8 = open.length;
    if ($[22] !== adding || $[23] !== canManage) {
      t9 = canManage && !adding ? <Button size="xs" variant="ghost" onClick={() => setAdding(true)}><Plus className="size-3.5" />Задача</Button> : null;
      $[22] = adding;
      $[23] = canManage;
      $[24] = t9;
    } else {
      t9 = $[24];
    }
    t1 = "flex flex-col gap-2";
    if ($[25] !== adding || $[26] !== applicationId || $[27] !== assignees) {
      t2 = adding && <TaskForm applicationId={applicationId} assignees={assignees} onDone={() => setAdding(false)} />;
      $[25] = adding;
      $[26] = applicationId;
      $[27] = assignees;
      $[28] = t2;
    } else {
      t2 = $[28];
    }
    t3 = open.length === 0 && !adding && <p className="text-sm text-muted-foreground">Открытых задач нет</p>;
    let t11;
    if ($[29] !== canManage || $[30] !== remove || $[31] !== toggle || $[32] !== update) {
      t11 = task_2 => <TaskRow key={task_2.id} task={task_2} canManage={canManage} busy={update.isPending || remove.isPending} onToggle={() => toggle(task_2)} onDelete={() => remove.mutate(task_2.id, {
        onError: e_0 => setError(errorText(e_0))
      })} />;
      $[29] = canManage;
      $[30] = remove;
      $[31] = toggle;
      $[32] = update;
      $[33] = t11;
    } else {
      t11 = $[33];
    }
    const t12 = open.map(t11);
    if ($[34] !== t12) {
      t4 = <ul className="flex flex-col">{t12}</ul>;
      $[34] = t12;
      $[35] = t4;
    } else {
      t4 = $[35];
    }
    t5 = done.length > 0 && <div><button type="button" className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setShowDone(_temp4)}>{showDone ? "\u0421\u043A\u0440\u044B\u0442\u044C \u0432\u044B\u043F\u043E\u043B\u043D\u0435\u043D\u043D\u044B\u0435" : `Выполненные · ${done.length}`}</button>{showDone && <ul className="mt-1 flex flex-col">{done.map(task_3 => <TaskRow key={task_3.id} task={task_3} canManage={canManage} busy={update.isPending || remove.isPending} onToggle={() => toggle(task_3)} onDelete={() => remove.mutate(task_3.id, {
          onError: e_1 => setError(errorText(e_1))
        })} />)}</ul>}</div>;
    $[0] = adding;
    $[1] = applicationId;
    $[2] = assignees;
    $[3] = canManage;
    $[4] = remove;
    $[5] = showDone;
    $[6] = tasks;
    $[7] = update;
    $[8] = T0;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
  } else {
    T0 = $[8];
    t1 = $[9];
    t2 = $[10];
    t3 = $[11];
    t4 = $[12];
    t5 = $[13];
    t6 = $[14];
    t7 = $[15];
    t8 = $[16];
    t9 = $[17];
  }
  let t10;
  if ($[36] !== error) {
    t10 = <FieldError>{error}</FieldError>;
    $[36] = error;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  let t11;
  if ($[38] !== t1 || $[39] !== t10 || $[40] !== t2 || $[41] !== t3 || $[42] !== t4 || $[43] !== t5) {
    t11 = <div className={t1}>{t2}{t3}{t4}{t5}{t10}</div>;
    $[38] = t1;
    $[39] = t10;
    $[40] = t2;
    $[41] = t3;
    $[42] = t4;
    $[43] = t5;
    $[44] = t11;
  } else {
    t11 = $[44];
  }
  let t12;
  if ($[45] !== T0 || $[46] !== t11 || $[47] !== t6 || $[48] !== t7 || $[49] !== t8 || $[50] !== t9) {
    t12 = <T0 title={t6} icon={t7} count={t8} action={t9}>{t11}</T0>;
    $[45] = T0;
    $[46] = t11;
    $[47] = t6;
    $[48] = t7;
    $[49] = t8;
    $[50] = t9;
    $[51] = t12;
  } else {
    t12 = $[51];
  }
  return t12;
}
function _temp4(v) {
  return !v;
}
function _temp3(task_0) {
  return task_0.doneAt;
}
function _temp2(task) {
  return !task.doneAt;
}
function TaskRow(t0) {
  const $ = _c(35);
  const {
    task,
    canManage,
    busy,
    onToggle,
    onDelete
  } = t0;
  const Icon = TASK_ICON[task.kind];
  const t1 = !canManage || busy;
  const t2 = task.doneAt ? `Вернуть в работу: ${task.title}` : `Отметить выполненной: ${task.title}`;
  let t3;
  if ($[0] !== task.doneAt) {
    t3 = task.doneAt ? <CheckCircle2 className="size-4 text-emerald-600" /> : <Circle className="size-4" />;
    $[0] = task.doneAt;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== onToggle || $[3] !== t1 || $[4] !== t2 || $[5] !== t3) {
    t4 = <button type="button" disabled={t1} onClick={onToggle} aria-label={t2} className="mt-0.5 shrink-0 text-muted-foreground transition-colors hover:text-primary disabled:pointer-events-none">{t3}</button>;
    $[2] = onToggle;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = task.doneAt && "text-muted-foreground line-through";
  let t6;
  if ($[7] !== t5) {
    t6 = cn("text-sm break-words", t5);
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t6 || $[10] !== task.title) {
    t7 = <p className={t6}>{task.title}</p>;
    $[9] = t6;
    $[10] = task.title;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== task) {
    t8 = <p className="text-xs"><DueLabel task={task} /></p>;
    $[12] = task;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== Icon) {
    t9 = <Icon className="size-3 shrink-0" />;
    $[14] = Icon;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const t10 = CRM_TASK_KIND_LABELS[task.kind];
  const t11 = task.assignee && ` · ${task.assignee.name}`;
  let t12;
  if ($[16] !== t10 || $[17] !== t11) {
    t12 = <span className="truncate">{t10}{t11}</span>;
    $[16] = t10;
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== t12 || $[20] !== t9) {
    t13 = <p className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground">{t9}{t12}</p>;
    $[19] = t12;
    $[20] = t9;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t13 || $[23] !== t7 || $[24] !== t8) {
    t14 = <div className="min-w-0 flex-1">{t7}{t8}{t13}</div>;
    $[22] = t13;
    $[23] = t7;
    $[24] = t8;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== busy || $[27] !== canManage || $[28] !== onDelete || $[29] !== task.title) {
    t15 = canManage && <button type="button" disabled={busy} onClick={onDelete} aria-label={`Удалить задачу: ${task.title}`} className="shrink-0 rounded p-1 text-muted-foreground opacity-100 transition-opacity hover:text-destructive md:opacity-0 md:group-hover:opacity-100 md:focus-visible:opacity-100"><Trash2 className="size-3.5" /></button>;
    $[26] = busy;
    $[27] = canManage;
    $[28] = onDelete;
    $[29] = task.title;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t14 || $[32] !== t15 || $[33] !== t4) {
    t16 = <li className="group flex items-start gap-2 rounded-md py-1.5">{t4}{t14}{t15}</li>;
    $[31] = t14;
    $[32] = t15;
    $[33] = t4;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  return t16;
}

/** Новая задача: вид, что сделать, срок пресетом или своей датой, кто делает. */
export function TaskForm(t0) {
  const $ = _c(49);
  const {
    applicationId,
    assignees,
    onDone
  } = t0;
  const add = useAddTask(applicationId);
  const [kind, setKind] = useState("call");
  const [title, setTitle] = useState("");
  const [dueAt, setDueAt] = useState(_temp5);
  const [assigneeId, setAssigneeId] = useState("");
  const [error, setError] = useState(null);
  let t1;
  if ($[0] !== add || $[1] !== assigneeId || $[2] !== dueAt || $[3] !== kind || $[4] !== onDone || $[5] !== title) {
    t1 = async () => {
      setError(null);
      const input = {
        title: title.trim() || CRM_TASK_KIND_LABELS[kind],
        kind,
        dueAt,
        ...(assigneeId ? {
          assigneeId
        } : {})
      };
      ;
      try {
        await add.mutateAsync(input);
        onDone();
      } catch (t2) {
        const e = t2;
        setError(errorText(e));
      }
    };
    $[0] = add;
    $[1] = assigneeId;
    $[2] = dueAt;
    $[3] = kind;
    $[4] = onDone;
    $[5] = title;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const submit = t1;
  let t2;
  if ($[7] !== submit) {
    t2 = event => {
      event.preventDefault();
      submit();
    };
    $[7] = submit;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = e_0 => setKind(e_0.target.value as CrmTaskKind);
    t4 = CRM_TASK_KINDS.map(_temp6);
    $[9] = t3;
    $[10] = t4;
  } else {
    t3 = $[9];
    t4 = $[10];
  }
  let t5;
  if ($[11] !== kind) {
    t5 = <Select aria-label={"\u0412\u0438\u0434 \u0437\u0430\u0434\u0430\u0447\u0438"} className="h-8" value={kind} onChange={t3}>{t4}</Select>;
    $[11] = kind;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const t6 = CRM_TASK_KIND_LABELS[kind];
  let t7;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = e_1 => setTitle(e_1.target.value);
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t6 || $[15] !== title) {
    t8 = <Input aria-label={"\u0427\u0442\u043E \u0441\u0434\u0435\u043B\u0430\u0442\u044C"} className="h-8" placeholder={t6} value={title} onChange={t7} />;
    $[14] = t6;
    $[15] = title;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t5 || $[18] !== t8) {
    t9 = <div className="grid gap-2 sm:grid-cols-[11rem_minmax(0,1fr)]">{t5}{t8}</div>;
    $[17] = t5;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== dueAt) {
    t10 = <DuePicker value={dueAt} onChange={setDueAt} />;
    $[20] = dueAt;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  let t12;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = e_2 => setAssigneeId(e_2.target.value);
    t12 = <option value="">Ответственный заявки</option>;
    $[22] = t11;
    $[23] = t12;
  } else {
    t11 = $[22];
    t12 = $[23];
  }
  let t13;
  if ($[24] !== assignees) {
    t13 = assignees.map(_temp7);
    $[24] = assignees;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== assigneeId || $[27] !== t13) {
    t14 = <Select aria-label={"\u041A\u0442\u043E \u0434\u0435\u043B\u0430\u0435\u0442"} className="h-8" value={assigneeId} onChange={t11}>{t12}{t13}</Select>;
    $[26] = assigneeId;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== error) {
    t15 = <FieldError>{error}</FieldError>;
    $[29] = error;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== add.isPending) {
    t16 = add.isPending && <Spinner />;
    $[31] = add.isPending;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== add.isPending || $[34] !== t16) {
    t17 = <Button type="submit" size="sm" disabled={add.isPending}>{t16}Поставить</Button>;
    $[33] = add.isPending;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== add.isPending || $[37] !== onDone) {
    t18 = <Button type="button" size="sm" variant="ghost" disabled={add.isPending} onClick={onDone}>Отмена</Button>;
    $[36] = add.isPending;
    $[37] = onDone;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== t17 || $[40] !== t18) {
    t19 = <div className="flex gap-2">{t17}{t18}</div>;
    $[39] = t17;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== t10 || $[43] !== t14 || $[44] !== t15 || $[45] !== t19 || $[46] !== t2 || $[47] !== t9) {
    t20 = <form className="flex flex-col gap-2 rounded-lg border bg-muted/20 p-3" onSubmit={t2}>{t9}{t10}{t14}{t15}{t19}</form>;
    $[42] = t10;
    $[43] = t14;
    $[44] = t15;
    $[45] = t19;
    $[46] = t2;
    $[47] = t9;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  return t20;
}
function _temp7(assignee) {
  return <option key={assignee.id} value={assignee.id}>{assignee.name}</option>;
}
function _temp6(key) {
  return <option key={key} value={key}>{CRM_TASK_KIND_LABELS[key]}</option>;
}
function _temp5() {
  return presetDue("tomorrow");
}